// Fixes a real "wrong clock" bug: both SQLite's CURRENT_TIMESTAMP and
// MySQL's DATETIME columns (as read back by the server with
// `dateStrings: true`, post-migration) produce a naive "YYYY-MM-DD
// HH:MM:SS" string with NO timezone marker. That value is actually UTC
// (SQLite always used UTC for CURRENT_TIMESTAMP; the server's MySQL pool is
// explicitly configured with `timezone: 'Z'`, see server/database.js), but
// when a browser parses a string like that with `new Date(...)`, it treats
// it as LOCAL time instead of UTC — so no timezone conversion happens, and
// the time shown is the raw UTC clock, not the real local time. For India
// (UTC+5:30) that means every such timestamp displays about 5.5 hours
// EARLIER than it actually happened (e.g. a payment made at ~10:52am IST
// shows up as "5:22am").
//
// This covers every naive-format timestamp the API returns, including
// payments.payment_date — which, before the MySQL migration, was a proper
// ISO string with a 'Z' (set via `new Date().toISOString()` server-side)
// and hit the "already has Z" branch below instead; it's still handled
// correctly here either way. Only a value that already has an explicit
// 'Z'/offset is left alone; everything else is assumed to be a naive UTC
// string and gets one added.
export function parseServerDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  var str = String(value);
  // Already has an explicit UTC/offset marker — parse as-is.
  if (/Z$|[+-]\d\d:\d\d$/.test(str)) return new Date(str);
  // Date-only strings ("YYYY-MM-DD", no time) are handled correctly by
  // every browser already (treated as UTC midnight, which for India's
  // positive UTC offset never shifts the calendar date) — no fix needed.
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return new Date(str);
  // Naive "YYYY-MM-DD HH:MM:SS" (SQLite's CURRENT_TIMESTAMP format) — mark
  // it as UTC explicitly before parsing.
  return new Date(str.replace(' ', 'T') + 'Z');
}

// Fixes a real "reports start on the wrong day" bug (found 2026-08-27):
// every date-range default on P&L Sheet, Income, Expenses, Dashboard, etc.
// was built as `new Date(y, m, 1).toISOString().split('T')[0]` — but
// `new Date(y, m, 1)` constructs LOCAL midnight, and `.toISOString()`
// converts that to UTC before formatting. For India (UTC+5:30), local
// midnight on the 1st is 18:30 UTC on the LAST day of the PREVIOUS month, so
// the date string produced was always one day early (e.g. "1 Aug" became
// "31 Jul"). This never showed up as a rounding/off-by-one only sometimes —
// it was wrong on every single page load, every month, regardless of the
// time of day, because it's the date construction itself that's wrong, not
// the clock. Fixed by formatting the LOCAL calendar fields directly instead
// of ever converting through UTC.
function pad2(n) { return String(n).padStart(2, '0'); }

export function todayLocalStr() {
  var d = new Date();
  return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate());
}

export function firstOfMonthLocalStr() {
  var d = new Date();
  return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-01';
}
